/**
 * Index of all Stoic Journal articles.
 *
 * Adding a new article = append one entry here + drop two markdown files at:
 *    /app/frontend/public/content/journal/{slug}/en.md
 *    /app/frontend/public/content/journal/{slug}/nl.md
 *
 * Sort order: `publishedAt` desc.
 *
 *  slug          — URL slug, kebab-case
 *  category      — one of CATEGORY_SLUGS (must match)
 *  publishedAt   — ISO date; used for sort + Schema.org datePublished
 *  readingTimeMin— integer minutes
 *  cover         — public path or absolute URL of the hero image
 *  title         — { en, nl }
 *  excerpt       — { en, nl }   short teaser shown on the index card
 *  author        — { en, nl }   defaults to "Ed Korporaal" but kept per-article for flexibility
 *  authorRole    — { en, nl }
 *  featured      — boolean; the most recent featured article renders large at the top
 */

export const CATEGORY_SLUGS = [
  "all",
  "marcus-aurelius",
  "epictetus",
  "seneca",
  "stoic-practice",
  "virtues",
  "modern-life",
  "stoicism-society",
  "virtue-guide",
];

// i18n keys for category labels (already in translations.js under journal.cat.*)
export const CATEGORY_LABEL_KEYS = {
  "all": "journal.cat.all",
  "marcus-aurelius": "journal.cat.marcus",
  "epictetus": "journal.cat.epictetus",
  "seneca": "journal.cat.seneca",
  "stoic-practice": "journal.cat.stoicPractice",
  "virtues": "journal.cat.virtues",
  "modern-life": "journal.cat.modernLife",
  "stoicism-society": "journal.cat.stoicismSociety",
  "virtue-guide": "journal.cat.virtueGuide",
};

export const ARTICLES = [
  {
    slug: "how-ancient-wisdom-helped-me",
    category: "stoic-practice",
    publishedAt: "2026-02-15",
    readingTimeMin: 9,
    cover:
      "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/rfevimtz_IMG_4855.jpeg",
    title: {
      en: "How Ancient Wisdom Helped Me Through a Difficult Period",
      nl: "Hoe oude wijsheid mij hielp door een moeilijke periode",
    },
    excerpt: {
      en:
        "On the night I could not sleep, I opened Marcus Aurelius. What followed was not relief, but a slow, patient reordering of the inside of my life.",
      nl:
        "Op de nacht dat ik niet kon slapen, sloeg ik Marcus Aurelius open. Wat volgde was geen opluchting, maar een trage, geduldige herordening van het binnenste van mijn leven.",
    },
    author: { en: "Ed Korporaal", nl: "Ed Korporaal" },
    authorRole: { en: "Founder, StoicOs.ai", nl: "Oprichter, StoicOs.ai" },
    tags: ["Marcus Aurelius", "Wisdom", "Memento Mori"],
    featured: true,
  },
];

export function findArticleBySlug(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}

export function sortedArticles(filterCategory = "all") {
  const list =
    filterCategory === "all"
      ? ARTICLES.slice()
      : ARTICLES.filter((a) => a.category === filterCategory);
  return list.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
}

// ---------------------------------------------------------------------------
// Remote (DB-backed) articles — published via /admin/journal
// ---------------------------------------------------------------------------

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/** Normalise a DB doc from `/api/journal/articles` into the shape the
 *  Journal UI already expects (matches `ARTICLES` entries). Content lives
 *  under `body` (Markdown, per locale) which the article page uses directly. */
export function normalizeDBArticle(doc) {
  if (!doc) return null;
  return {
    slug: doc.slug,
    category: doc.category,
    publishedAt: doc.published_at || doc.updated_at || doc.created_at,
    readingTimeMin: doc.reading_time_min || 5,
    cover: doc.cover || "",
    coverMeta: doc.cover_meta || null,
    coverFit: doc.cover_fit || "cover",
    title: doc.title || { en: "", nl: "" },
    excerpt: doc.excerpt || { en: "", nl: "" },
    body: doc.body || { en: "", nl: "" },
    author: {
      en: (doc.author && doc.author.name) || "Ed Korporaal",
      nl: (doc.author && doc.author.name) || "Ed Korporaal",
    },
    authorRole: {
      en: (doc.author && doc.author.role) || "Founder, StoicOs.ai",
      nl: (doc.author && doc.author.role) || "Oprichter, StoicOs.ai",
    },
    tags: doc.tags || [],
    featured: !!doc.featured,
    metaDescription: doc.meta_description || { en: "", nl: "" },
    articleReference: doc.article_reference || "",
    _source: "db",
  };
}

/** Fetch the full list of published articles (from the API) once, cache
 *  per session, and merge with the file-based ARTICLES. */
let _cachePromise = null;
export function fetchAllArticles({ force = false } = {}) {
  if (!force && _cachePromise) return _cachePromise;
  const url = `${API_ROOT}/api/journal/articles`;
  _cachePromise = fetch(url, { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : { articles: [] }))
    .then((d) => (d.articles || []).map(normalizeDBArticle))
    .catch(() => [])
    .then((remote) => {
      // Merge — DB slug wins if collision.
      const bySlug = new Map();
      ARTICLES.forEach((a) => bySlug.set(a.slug, { ...a, _source: "file" }));
      remote.forEach((a) => bySlug.set(a.slug, a));
      const merged = Array.from(bySlug.values());
      merged.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
      return merged;
    });
  return _cachePromise;
}

/** Fetch a single DB-backed article by slug (returns null if not found). */
export async function fetchArticleBySlug(slug) {
  try {
    const r = await fetch(`${API_ROOT}/api/journal/articles/${encodeURIComponent(slug)}`, {
      cache: "no-store",
    });
    if (!r.ok) return null;
    const doc = await r.json();
    return normalizeDBArticle(doc);
  } catch {
    return null;
  }
}
